import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useMaintenanceInsuranceDue } from '@/hooks/use-asset-management-widgets';
import { Calendar, Wrench, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isBefore, startOfDay } from 'date-fns';

export function MaintenanceInsuranceDueCalendarWidget() {
  const { data, isLoading, error } = useMaintenanceInsuranceDue(30);
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Maintenance & Insurance Due</CardTitle>
              <p className="text-sm text-muted-foreground">Upcoming due dates</p>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Handle error state (API failure)
  if (error) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Maintenance & Insurance Due</CardTitle>
              <p className="text-sm text-muted-foreground">Upcoming due dates</p>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            Unable to load due items
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle empty data state (no items in database)
  if (!data) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Maintenance & Insurance Due</CardTitle>
              <p className="text-sm text-muted-foreground">Upcoming due dates</p>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col h-[300px] items-center justify-center text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No upcoming items
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const today = new Date();
  const monthStart = startOfMonth(today);
  const monthEnd = endOfMonth(today);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Group items by date
  const itemsByDate = new Map<string, typeof data.items>();
  data.items.forEach(item => {
    const dateKey = format(new Date(item.dueDate), 'yyyy-MM-dd');
    if (!itemsByDate.has(dateKey)) {
      itemsByDate.set(dateKey, []);
    }
    itemsByDate.get(dateKey)!.push(item);
  });

  const getDayStatus = (day: Date) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const items = itemsByDate.get(dateKey);
    if (!items || items.length === 0) return null;

    const hasOverdue = items.some(item => item.daysUntilDue < 0);
    const hasDueToday = items.some(item => item.daysUntilDue === 0);
    const hasUrgent = items.some(item => item.daysUntilDue > 0 && item.daysUntilDue <= 7);

    if (hasOverdue || hasDueToday) return 'critical';
    if (hasUrgent) return 'warning';
    return 'normal';
  };

  const getTypeIcon = (type: string) => {
    return type === 'Maintenance' ? (
      <Wrench className="h-3.5 w-3.5 text-blue-500" />
    ) : (
      <Shield className="h-3.5 w-3.5 text-green-500" />
    );
  };

  const handleAssetClick = (assetId: string) => {
    navigate(`/assets/${assetId}`);
  };

  const getDaysUntilBadge = (daysUntil: number) => {
    if (daysUntil < 0) {
      return <Badge variant="destructive" className="text-[10px] h-4 py-0">Overdue</Badge>;
    } else if (daysUntil === 0) {
      return <Badge variant="destructive" className="text-[10px] h-4 py-0">Today</Badge>;
    } else if (daysUntil <= 7) {
      return <Badge variant="destructive" className="text-[10px] h-4 py-0">{daysUntil}d</Badge>;
    } else if (daysUntil <= 30) {
      return <Badge variant="default" className="bg-amber-500 text-[10px] h-4 py-0">{daysUntil}d</Badge>;
    } else {
      return <Badge variant="secondary" className="text-[10px] h-4 py-0">{daysUntil}d</Badge>;
    }
  };

  // Get first day of week offset
  const firstDayOfMonth = monthStart.getDay();
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Card className="relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Maintenance & Insurance Due</CardTitle>
            <p className="text-sm text-muted-foreground">{format(today, 'MMMM yyyy')}</p>
          </div>
          <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {/* Two Column Layout: 2/3 Calendar + 1/3 List */}
        <div className="grid grid-cols-3 gap-4">
          {/* Calendar - 2 columns */}
          <div className="col-span-2">
            {/* Calendar Header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {daysOfWeek.map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells for days before month starts */}
              {Array.from({ length: firstDayOfMonth }, (_, i) => `empty-day-${i}`).map((id) => (
                <div key={id} className="aspect-square" />
              ))}

              {/* Calendar days */}
              {daysInMonth.map(day => {
                const status = getDayStatus(day);
                const dateKey = format(day, 'yyyy-MM-dd');
                const items = itemsByDate.get(dateKey) || [];
                const isPast = isBefore(day, startOfDay(today)) && !isToday(day);

                return (
                  <div
                    key={day.toISOString()}
                    className={`
                      aspect-square p-1 rounded-md border text-center relative
                      ${isToday(day) ? 'border-primary border-2 bg-primary/5' : 'border-border'}
                      ${isPast ? 'opacity-40' : ''}
                      ${status === 'critical' ? 'bg-red-50 dark:bg-red-950/30' : ''}
                      ${status === 'warning' ? 'bg-amber-50 dark:bg-amber-950/30' : ''}
                      ${status === 'normal' ? 'bg-blue-50 dark:bg-blue-950/30' : ''}
                    `}
                  >
                    <div className="text-xs font-medium">{format(day, 'd')}</div>
                    {items.length > 0 && (
                      <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 flex gap-0.5">
                        {items.slice(0, 3).map((item) => (
                          <div
                            key={`${item.assetId}-${item.type}`}
                            className={`w-1 h-1 rounded-full ${
                              item.type === 'Maintenance' ? 'bg-blue-500' : 'bg-green-500'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mt-3 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-muted-foreground">Maintenance</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-muted-foreground">Insurance</span>
              </div>
            </div>
          </div>

          {/* Top 4 Items List - 1 column */}
          <div className="space-y-2">
            <div className="text-sm font-semibold">Upcoming</div>
            {data.items.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                No upcoming items
              </div>
            ) : (
              <div className="space-y-1.5">
                {data.items.slice(0, 4).map((item) => (
                  <button
                    type="button"
                    key={`${item.assetId}-${item.type}-${item.dueDate}`}
                    className="rounded-md border p-2 hover:bg-accent cursor-pointer transition-colors group w-full text-left"
                    onClick={() => handleAssetClick(item.assetId)}
                    aria-label={`View ${item.assetName} ${item.type.toLowerCase()} details`}
                  >
                    <div className="flex items-start gap-2 mb-1">
                      {getTypeIcon(item.type)}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs truncate">{item.assetName}</div>
                        <div className="text-[10px] text-muted-foreground">{format(new Date(item.dueDate), 'MMM d')}</div>
                      </div>
                      {getDaysUntilBadge(item.daysUntilDue)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
